import cn from 'classnames';
import React, { ButtonHTMLAttributes, useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Icon, IconName } from '../Icon';
import { ReactComponent as Underline } from './Underline.svg';

/**
 * Custom hook to safely detect if component is rendered within React Router context
 * Returns true if Router context is available, false otherwise
 */
const useIsInRouterContext = (): boolean => {
  try {
    useHistory();
    return true;
  } catch {
    return false;
  }
};

export enum ButtonType {
  Primary = 'primary',
  Secondary = 'secondary',
  Tertiary = 'tertiary',
  TextButton = 'textButton',
  TextLink = 'textLink',
}

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'onClick'> {
  align?: 'center' | 'edges';
  buttonType?: ButtonType;
  darkBackground?: boolean;
  iconName?: IconName;
  iconPosition?: 'left' | 'right';
  // Defining onClick as optional with no arguments to handle being shared between both button and link
  onClick?: () => void;
  renderFullWidth?: boolean;
  to?: string;
}

/**
 * Button component that can be used as a button or a link.
 *
 * @param {ButtonProps} props - The props for the button component.
 * @param {to} string - The URL to navigate to when the button is clicked.
 *        If provided, the Button component will return a Link component instead of a button.
 *        *N.B.* This will not work if the button is rendered outside of React Router, i.e. in the modal component.
 *        In that case, you should use the onClick prop with history.push to navigate to the desired URL.
 * @param {onClick} function - This prop will handle both button and link clicks.
 *        Because of this, no click event is returned.
 * @returns {React.FC<ButtonProps>} The button component.
 */
const Button: React.FC<ButtonProps> = ({
  align,
  buttonType = ButtonType.Primary,
  children,
  darkBackground,
  disabled = false,
  iconName,
  iconPosition = 'right',
  id,
  renderFullWidth = false,
  onClick,
  to,
  ...props
}) => {
  const isInRouter = useIsInRouterContext();
  const isTextButton = [ButtonType.TextButton, ButtonType.TextLink].includes(buttonType);
  const [isLoading, setIsLoading] = useState(false);
  const isDisabled = disabled || isLoading;

  /**
   * Calling setIsLoading after an async operation may attempt to update state on an unmounted component.
   * This track whether the component is mounted using a ref and a cleanup effect.
   * In `handleClick`, we call setIsLoading if the component is still mounted.
   */
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  /**
   * Automatically sets the loading state to true when the button is clicked.
   * This is useful for buttons that are used to trigger asynchronous actions.
   */
  const handleClick = async () => {
    // Gets rid of possible race window between calling setIsLoading & React re-rendering to apply the disabled state
    if (isLoading) return;

    setIsLoading(true);
    try {
      await onClick?.();
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  };

  const sharedProps = {
    id,
    onClick: handleClick,
    disabled: isDisabled,
    className: cn('box-border cursor-pointer gap-1 text-caption-600 outline-none', {
      /* Common Button Styles for all types except TextLink */
      'box-border flex h-[45px] w-full items-center rounded-xl px-6': !isTextButton,

      /* Primary Button Styles */
      [`border-0 bg-raspberry text-milk 
        hover:bg-raspberry-hover 
        focus-visible:border-3 focus-visible:border-solid focus-visible:border-raspberry-active 
        active:bg-raspberry-active
        `]: buttonType === ButtonType.Primary && !isDisabled,

      /* Primary Button Disabled Styles */
      'bg-eggplant-100 text-white': Boolean(isDisabled && buttonType === ButtonType.Primary),

      /* Secondary Button Styles */
      [`border-3 border-eggplant-50 bg-milk text-eggplant-500
        hover:border-eggplant-200 
        focus-visible:border-eggplant-500 
        active:border-eggplant-500 active:bg-eggplant-50`]: buttonType === ButtonType.Secondary && !isDisabled,

      /* Secondary Button Disabled Styles */
      'border-3 border-eggplant-50 bg-milk text-eggplant-200': buttonType === ButtonType.Secondary && isDisabled,

      /* Tertiary Button Styles */
      [`border-3 border-solid border-eggplant-50 bg-rice text-eggplant-500
        hover:border-eggplant-200 
        focus-visible:border-eggplant-500 
        active:border-eggplant-300`]: buttonType === ButtonType.Tertiary && !isDisabled,

      /* Tertiary Button Disabled Styles */
      'border-3 border-eggplant-50 bg-rice text-eggplant-200': buttonType === ButtonType.Tertiary && isDisabled,

      /* Text Link Common Styles */
      'inline text-nowrap border-none': isTextButton,

      /* Text Link Standard Styles */
      [`text-eggplant-500 
        hover:text-raspberry 
        focus-visible:text-caption-700 focus-visible:text-raspberry-active
        active:text-raspberry-active`]: isTextButton && !darkBackground && !isDisabled,

      /* Text Link Standard Disabled Styles */
      'text-eggplant-200': isTextButton && !darkBackground && isDisabled,

      /* Text Link Dark Background Styles  */
      [`text-rice 
        hover:text-raspberry 
        focus-visible:text-caption-700 focus-visible:text-raspberry-active 
        active:text-raspberry-active`]: isTextButton && darkBackground && !isDisabled,

      /* Text Link Dark Background Disabled Styles  */
      'text-eggplant-300': isTextButton && darkBackground && isDisabled,

      /* Other styles */
      'max-w-[400px]': !renderFullWidth && !isTextButton,
      'w-full': renderFullWidth && !isTextButton,
    }),
  };

  const buttonContent = (
    <span
      id={id}
      className={cn('flex w-full items-center gap-1', {
        /* Icon Position Styles */
        'flex-row-reverse': iconPosition === 'left',
        'justify-between': align === 'edges',
        'justify-center': align !== 'edges',
      })}>
      <span id={id} className='relative'>
        {children}
        {buttonType === ButtonType.TextLink && <Underline className='absolute -bottom-1 left-0 h-1 w-full' />}
      </span>

      {isLoading ? (
        <i className='fa-solid fa-circle-notch fa-spin' id={id} />
      ) : (
        iconName && !isLoading && <Icon name={iconName} size={16} id={id} />
      )}
    </span>
  );

  /**
   * If Button is rendered outside of a ReactRouter component (i.e. in a modal)
   * return a button instead of a Link which will throw an error and crash the page
   */
  return !!to && isInRouter ? (
    <Link to={to} {...sharedProps}>
      {buttonContent}
    </Link>
  ) : (
    <button {...props} {...sharedProps}>
      {buttonContent}
    </button>
  );
};

export default Button;
